import * as React from 'react';
import { Box, IconButton, Modal } from '@mui/material';
import * as Plotly from 'plotly.js';
import Plot from 'react-plotly.js';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import { IParameter, parameterMap } from './parametermapping';
import { useTranslation } from 'react-i18next';
import DataCtx from './api/DataCtx';
import { formatValue } from './utils';
import FetchApiCtx from './api/FetchApiCtx';
import ISeriesDataResponse from './api/ISeriesDataResponse';

interface Props {
    selectedParameterId: number | undefined, 
    setSelectedParameterId: (filter: number | undefined) => void,
    timezoneLabel: string
}

export default function ParameterPopover(props: Props) {

    const { t, i18n } = useTranslation();

    const api = React.useContext(FetchApiCtx);

    const parameter: IParameter = parameterMap.filter(parameter => parameter.id == props.selectedParameterId)[0];

    const data = React.useContext(DataCtx);
    const record = data.params.filter(p => p.issue === parameter.issue)[0];
    const formattedValue = formatValue(record, parameter.decimalDigits, parameter.calc)

    const [ seriesData, setSeriesData ] = React.useState<ISeriesDataResponse | undefined>(undefined);

    let traces: Partial<Plotly.ScatterData>[] = [];

    const handleClose = () => {
        props.setSelectedParameterId(undefined);
    };

    React.useEffect(() => {

        try {
            api.getSeriesData(parameter.issue).then(data => setSeriesData(data));
        } catch (error) {
            console.error('[ParameterPopover] Error when getting seriesData: ', error)
        }
        
    }, []);

    const getDataLayout = (color) => {

        return ({
            // type: 'scatter',
            // regarding compatibility between rangeslider and scattergl: https://github.com/plotly/plotly.js/issues/2627
            type: 'scattergl',
            mode: 'lines+markers',
            line: {
                dash: 'solid',
                width: 2,
                opacity: 0.7,
                color: color.replace('rgb', 'rgba').replace(')', ',0.5)')
            },
            marker: {
                size: 3.5,
                color: color
            }
        } as Partial<Plotly.ScatterData>)
    }

    const getXaxisRange = (hoursOi: number) => {

        const currentDatetime = moment();

        // Set minimum to hoursOi prior to currentDatetime
        const xMin = currentDatetime.clone().subtract({"hours": hoursOi}).format("YYYY-MM-DD HH:mm:ssZ");

        // Set maximum to currentDatetime
        const xMax = currentDatetime.clone().add({"hours": 0}).format("YYYY-MM-DD HH:mm:ssZ");

        return [xMin, xMax]
    }

    var traceData: Partial<Plotly.ScatterData> = { 
        name: 'Historical', 
        x: seriesData ? seriesData.map(d => d[0]) : [], 
        y: seriesData ? seriesData.map(d => parameter.calc(d[1])) : [], 
        xaxis: 'x',
        yaxis: 'y',
        ...getDataLayout('rgb(17, 138, 200)'),
    };

    traces = [...traces, traceData]

    var updateMenus = [
        {
            type: "buttons",
            direction: "right",
            xanchor: "left",
            x: -0.08,
            y: 1.2,
            showactive: true,
            active: -1,
            buttons: [
                {
                    label: '12 hours',
                    method: 'relayout',
                    args: [
                        { "xaxis.range": getXaxisRange(12)}
                    ],
                },
                {
                    label: '24 hours',
                    method: 'relayout',
                    args: [
                        { "xaxis.range": getXaxisRange(24)}
                    ],
                },
                {
                    label: '2 days',
                    method: 'relayout',
                    args: [
                        { "xaxis.range": getXaxisRange(24*2)}
                    ],
                },
                {
                    label: '7 days',
                    method: 'relayout',
                    args: [
                        { "xaxis.range": getXaxisRange(24*7)}
                    ],
                },
                {
                    label: 'All',
                    method: 'relayout',
                    args: [
                        { "xaxis.range": [
                                seriesData ? seriesData.at(0)[0] : 0, 
                                seriesData ? seriesData.at(-1)[0] : 1
                            ],
                        },
                    ],
                },
            ],
        }
    ]

    const layout: Partial<Plotly.Layout> = {
        uirevision: true, // do not change zoom when new data is loaded
        useResizeHandler: true,
        height: 370,
        autosize: true,
        margin: {
            l: 40,
            r: 40,
            b: 40,
            t: 10,
            pad: 0
        },
        hovermode: 'x+closest',
        hoverdistance: 20,
        updatemenus: updateMenus,
        xaxis: {
            title: {
                text: `Time [${props.timezoneLabel}]`,

            },
            tickfont: {
                size: 10
            },
            type: 'date',
            tickformat: '%Y-%m-%d<br>%H:%M',
            showspikes: true,
            spikemode: 'across',
            spikesnap: 'hovered data',
            showgrid: true,
            spikedash: 'solid',
            spikecolor: '#292929',
            spikethickness: 2,
            rangeslider: {
                // regarding compatibility between rangeslider and scattergl: https://github.com/plotly/plotly.js/issues/2627
                visible: false
            },
            autorange: true, // do not change zoom when new data is loaded
        },
        yaxis: {
            title: {
                text: t(parameter.label)
            },
            autorange: true, // do not change zoom when new data is loaded
        },
        legend: {
        },
    };

    return (
        <React.Fragment>
            <Modal
                open={props.selectedParameterId != undefined}
                onClose={handleClose}
            >
                <Box 
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80vw',
                        bgcolor: 'background.paper',
                        p: 4,
                        borderRadius: '4px'
                    }}
                >
                    {/* CLOSE */}
                    <Box sx={{ 
                        position: 'relative', 
                        zIndex: 1 
                    }}>
                        <IconButton
                            sx={{ 
                                position: 'absolute',
                                top: -32,
                                right: -32,
                            }}
                            color="info"
                            onClick={() => props.setSelectedParameterId(undefined)}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                    {/* <div className="datacelllabel">{t(parameter.label)}</div> */}
                    {/* <div className="datacellvalue">{formattedValue}</div> */}
                    <div className="datacelllabel" style={{marginLeft: 10}}>
                        {`${t(parameter.label)}: `}
                        <strong>{`${formattedValue}`}</strong>
                    </div>
                    <Plot
                        data={traces}
                        layout={layout}
                        useResizeHandler={true}
                        style={{width: "100%", height: "100%"}}
                    />
                </Box>
            </Modal>
        </React.Fragment>
    );
}
